'use client';

import { useState } from 'react';
import { IconChevronDown, IconArrowUpRight } from '@tabler/icons-react';
import LinkNoPrefetch from '@/components/link-noprefetch';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import customRenderers from '@/components/block-editor';
import safeJsonParse from '@/app/utils/safeJSONParse';
import { cn } from '@/app/utils/cn';

function FaqRow({ question, body, body_raw, open, onToggle }) {
    const parsedBody = body ?? safeJsonParse(body_raw);

    return (
        <div className="border-b border-white/10">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-6 py-6 text-left">
                <span className="text-base font-semibold text-white sm:text-lg">{question}</span>
                <IconChevronDown
                    size={20}
                    className={cn(
                        'shrink-0 text-slate-400 transition-transform duration-300',
                        open && 'rotate-180'
                    )}
                    aria-hidden
                />
            </button>
            <div
                className={cn(
                    'grid transition-all duration-300 ease-out',
                    open ? 'grid-rows-[1fr] pb-6 opacity-100' : 'grid-rows-[0fr] pb-0 opacity-0'
                )}>
                <div className="overflow-hidden">
                    {parsedBody && (
                        <div className="prose max-w-none prose-p:text-sm prose-p:text-slate-300 prose-p:last:mb-0 prose-a:text-fuschia-500">
                            <DotBlockEditor
                                customRenderers={customRenderers}
                                blocks={parsedBody}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function FaqSection({ faqHeading, faqBody, callToAction, faqSections }) {
    const sectionList = Array.isArray(faqSections) ? faqSections : [];
    const [button] = Array.isArray(callToAction) ? callToAction : [];
    const [openIdx, setOpenIdx] = useState(-1);

    return (
        <section className="background-dark py-20 lg:py-28" data-component="faq-section">
            <div className="container grid grid-cols-1 gap-12 lg:grid-cols-[2fr_3fr] lg:gap-16">
                <div className="flex flex-col gap-6">
                    {faqHeading && (
                        <h2 className="text-4xl font-bold leading-tight text-white lg:text-5xl">
                            {faqHeading}
                        </h2>
                    )}
                    {faqBody && (
                        <p className="max-w-md whitespace-pre-line text-base text-slate-300">
                            {faqBody}
                        </p>
                    )}
                    {button && button.urlName && button.url && (
                        <LinkNoPrefetch
                            href={button.url}
                            className="mt-2 inline-flex items-center gap-1 text-base font-semibold text-fuschia-600 underline decoration-fuschia-600 underline-offset-4 hover:text-fuschia-500 hover:decoration-fuschia-500">
                            <span>{button.urlName}</span>
                            <IconArrowUpRight size={18} aria-hidden />
                        </LinkNoPrefetch>
                    )}
                </div>

                {sectionList.length > 0 && (
                    <div className="flex flex-col">
                        {sectionList.map((item, i) => (
                            <FaqRow
                                key={item?.identifier ?? i}
                                question={item?.headline || item?.title}
                                body={item?.body}
                                body_raw={item?.body_raw}
                                open={openIdx === i}
                                onToggle={() => setOpenIdx((prev) => (prev === i ? -1 : i))}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

export default FaqSection;
